import { formatDayMonthTime } from "@/lib/dates";
import type { AppDetail, AppSummary, QuestionKind } from "@/lib/api/apps/types";
import type { StatusChipTone } from "@/components/common/StatusChip";

export const MICROS_PER_CENT = 10_000;

const DOLLARS = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

/** Every AI amount to a tenth of a cent: one model call often costs less
 *  than a cent, and "$0.000" would read as nothing spent. */
export const usdMicros = (micros: number) =>
  micros > 0 && micros < 500 ? "<$0.001" : DOLLARS.format(micros / 1_000_000);

export const usd = (cents: number) => usdMicros(cents * MICROS_PER_CENT);

const count = (n: number, one: string, many: string) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** "3 web lookups · 2 page reads"; page reads only when a paid engine read any. */
export const webCalls = (searches: number, reads: number) =>
  [
    count(searches, "web lookup", "web lookups"),
    ...(reads ? [count(reads, "page read", "page reads")] : []),
  ].join(" · ");

/** `12.50` for a form field. */
/** An amount to edit, to the same tenth of a cent `usd` shows, so clicking a figure changes only what holds it. */
export const dollarsText = (cents: number) => (cents / 100).toFixed(3);

/** Cents from what a person typed, or `null` for something that is not an
 *  amount. A comma is a decimal point. */
export function centsFrom(text: string): number | null {
  const n = Number.parseFloat(text.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

/** A design app's cost limit as typed, "3", "2.50" or "2,50": above $0 and
 *  at most $1,000 (the server's ceiling), else `null`. */
export function costLimitCents(text: string): number | null {
  const cents = centsFrom(text);
  return cents !== null && cents >= 1 && cents <= 100_000 ? cents : null;
}

/** Why an app's cost limit cannot be `cents`: the server's range, and never
 *  below what is already spent. */
export function costLimitRefusal(
  cents: number,
  spentCents: number,
): string | null {
  if (cents < 1 || cents > 100_000) return "Between $0.01 and $1,000";
  if (cents < spentCents) return `At least ${usd(spentCents)}, already spent`;
  return null;
}

/** "$12.40 of $50", or "$12.40" without a limit, and whether it is near. */
export function spendLabel(
  spentMicros: number,
  limitCents: number | null | undefined,
): { text: string; near: boolean } {
  if (limitCents == null) return { text: usdMicros(spentMicros), near: false };
  return {
    text: `${usdMicros(spentMicros)} of ${usd(limitCents)}`,
    near: spentMicros * 100 >= limitCents * MICROS_PER_CENT * 80,
  };
}

const compact = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** The ledger's token counts, as every usage breakdown labels them. */
export const TOKEN_KINDS = [
  ["Input", "input_tokens"],
  ["Cache read", "cache_read_tokens"],
  ["Cache write", "cache_write_tokens"],
  ["Output", "output_tokens"],
] as const;

/** "5.9K". */
export const countLabel = (n: number) => compact.format(n);

/** "Wed 7 Oct, 06:00" in the browser's zone. */
export function whenLabel(iso: string): string {
  const date = new Date(iso);
  const at = formatDayMonthTime(date);
  return (
    at && `${date.toLocaleDateString(undefined, { weekday: "short" })} ${at}`
  );
}

const PAUSE_REASONS: Record<string, string> = {
  cost_limit: "its total cost limit is spent",
  lost_access: "a lent table is out of reach",
  no_principal: "the app's identity was retired",
  no_owner: "the app has no owner; an org admin can appoint one",
  failing: "its steps keep failing",
  finish_asked:
    "two runs found nothing new; answer whether the brief is covered, then resume",
  no_model: "no model available",
  model_off: "its model was switched off; pick another",
  house_limit: "Mycorr's model is unavailable until its limit resets",
  trial_spent: "the trial on Mycorr's model is used up; pick your own key",
  provider_limit: "the provider key's monthly limit is reached",
  member_limit: "the monthly limit on the organization's keys is reached",
  paused: "",
};

/** The web lookups a research run may still make, ever. */
export function searchesLine({ used, limit }: { used: number; limit: number }) {
  if (limit <= 0) {
    return "Organizations don't include web lookups yet. Runs read pages and your tables.";
  }
  const left = Math.max(0, limit - used);
  return left > 0
    ? `${left} of ${limit} web lookups left.`
    : `Your ${limit} web lookups are used up. Runs carry on without them.`;
}

/** A run is due once `next_run_at` is this close; the scheduler starts it
 *  up to 30 s later, and a page that stopped polling never sees it begin. */
const RUN_DUE_WINDOW_MS = 2 * 60_000;

/** How often the app page re-reads the app: every 5 s while a run is live
 *  or due, every 2 s while a round of questions is, else not at all. */
export function detailPollMs(
  app: Pick<AppDetail, "status" | "current_run" | "next_run_at">,
  now: number = Date.now(),
): number | false {
  if (app.current_run) return app.current_run.kind === "ask" ? 2_000 : 5_000;
  if (app.status !== "active" && app.status !== "setup") return false;
  if (app.status === "setup") return app.next_run_at ? 2_000 : false;
  if (!app.next_run_at) return 5_000;
  const at = Date.parse(app.next_run_at);
  return Number.isNaN(at) || at - now <= RUN_DUE_WINDOW_MS ? 5_000 : false;
}

/** What the list and the header say about an app's state, in one place. */
export function statusLabel(app: AppSummary): {
  text: string;
  tone: StatusChipTone;
} {
  switch (app.status) {
    case "setup":
      return { text: "Setup not finished", tone: "warning" };
    case "finished":
      return { text: "Finished", tone: "muted" };
    case "deleted":
      return { text: "Deleted", tone: "muted" };
    case "paused": {
      const reason =
        PAUSE_REASONS[app.paused_reason ?? ""] ?? app.paused_reason;
      return {
        text: reason ? `Paused · ${reason}` : "Paused",
        tone: "warning",
      };
    }
    default:
      if (app.step === "ask")
        return { text: "Asking before the next run", tone: "accent" };
      if (!app.next_run_at)
        return { text: `Running · run ${app.run_count}`, tone: "accent" };
      return { text: `Next run ${whenLabel(app.next_run_at)}`, tone: "muted" };
  }
}

export const KIND_LABEL: Record<QuestionKind, string> = {
  same_entity: "Same entity?",
  scope: "Scope",
  definition: "Definition",
  not_found: "Not found",
  budget: "Budget",
  finish: "Finish?",
};

export const WEEKDAYS = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
] as const;

const END_REASONS: Record<string, string> = {
  stopped: "Stopped by a person",
  cost_limit: "The app's cost limit was reached",
  run_cap: "This run's cap was reached",
  house_limit: "MyCorr's monthly model limit was reached",
  provider_limit: "The model key's monthly limit was reached",
  member_limit: "The owner's monthly limit was reached",
  trial_spent: "The trial is used up",
  budget: "A spend limit was reached",
  max_turns: "The turn limit was reached",
  max_tool_calls: "The tool call limit was reached",
  max_time: "The time limit was reached",
  max_output: "The output limit was reached",
  model_key_refused: "The provider refused the model key",
  model_refused: "The provider refused the request",
  model_busy: "The model was busy or unreachable",
  model_failed: "The model call failed",
  step_crashed: "It stopped unexpectedly three times",
  refused: "The model declined",
  no_owner: "The app has no owner",
  no_principal: "The app's account is gone",
  no_model: "No model is available",
  model_off: "The app's model is switched off",
  lost_access: "A lent table is out of reach",
  done: "No questions this round",
};

/** Why a step ended, from the code every app kind stores. */
export const endReasonWords = (code: string) =>
  END_REASONS[code] ?? code.replaceAll("_", " ");
